import React, { useState, useRef, useEffect } from "react";
import { Button, Col, Row, Card, Label, Input, Spinner } from "reactstrap";
import { connect } from "react-redux";
import CustomModal from "../../../../../../../Components/Modals/CustomModal";
import FetchProjectAsinSkusListService from "../../../../../../../../Service/DashboardServices/ContentManagementServices/FetchProjectAsinSkusListService";
import fetchProductListingDataService from "../../../../../../../../Service/OPSServices/AmazonProductListing_v1/fetchProductListingDataService";
import patchListingFieldsService from "../../../../../../../../Service/OPSServices/AmazonProductListing_v1/patchListingFieldsService";
import AmazonProductListingUpdatePopup from "../../../../../ProductListingApi_v1/Components/AmazonProductListingUpdatePopup";


const AmazonListingImageUpdatePopup = (props) => {
    const [isUpdateListingPopupOpen, setIsUpdateListingPopupOpen] = useState(false);
    const [asinOptions, setAsinOptions] = useState([]);
    const [selectedAsin, setSelectedAsin] = useState("");
    const [skuOptions, setSkuOptions] = useState([]);
    const [selectedSku, setSelectedSku] = useState("");
    const [skuData, setSkuData] = useState({});
    const [imageAttributes, setImageAttributes] = useState([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const [selectedAttribute, setSelectedAttribute] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveModalData, setSaveModalData] = useState(null);
    const [validated, setValidated] = useState(false);
    const validateSectionRef = useRef(null);

    const resetImageState = () => {
        setImageAttributes([]);
        setFetchError("");
        setSelectedAttribute("");
        setSaveModalData(null);
    };

    const handleUpdateAmazonListing = () => {

        const asinArray = props.asinList
            ? props.asinList.split(",").map(item => item.trim())
            : [];

        setAsinOptions(asinArray);

        FetchProjectAsinSkusListService(props.token, {
            integerId: props.integerId,
            asinList: asinArray
        }).then((res) => {

            if (!res || res.error) return;

            setSkuData(res.skuData || {});

            if (asinArray.length === 1) {
                const asin = asinArray[0];
                setSelectedAsin(asin);
                setSkuOptions(res.skuData[asin] || []);
            } else {
                setSelectedAsin("");
                setSkuOptions([]);
            }

            setSelectedSku("");
            resetImageState();
            setIsUpdateListingPopupOpen(true);
        });
    };

    const handleSkuChange = (e) => {

        const sku = e.target.value;
        setSelectedSku(sku);
        resetImageState();

        if (!sku) return;

        const currentAsin = selectedAsin || asinOptions[0];

        setIsLoadingImages(true);

        fetchProductListingDataService(props.token, {
            asin: currentAsin,
            sku: sku,
            integerId: props.integerId,
            fetchImagesOnly: true
        })
            .then((res) => {
        
                if (!res || res.error) {
                    setFetchError(
                        (res && res.message) ||
                        "Unable to fetch listing images. Please try again."
                    );
                    return;
                }
        
                setImageAttributes(res.imageAttributes || []);
            })
            .catch((err) => {
                console.log(err);
                setFetchError("Something went wrong while fetching listing images.");
            })
            .finally(() => {
                setIsLoadingImages(false);
            });
        }

    // ---------- Select flow ----------
    const handleSelectAttribute = (attribute) => {
        setValidated(false);
        setSaveModalData(null);
        setSelectedAttribute((prev) => (prev === attribute ? "" : attribute));
    };

    // ---------- Validate (mode: VALIDATION_PREVIEW) — reused from optimization module ----------
    const handleValidateReplacement = async () => {

        if (!selectedAttribute || !props.replacementImage || !props.replacementImage.url) return;

        setSaving(true);
        setSaveModalData(null);

        const requestData = {
            asin: selectedAsin || asinOptions[0],
            sku: selectedSku,
            mode: "VALIDATION_PREVIEW",
            integerId: props.integerId,
            updates: [
                { field: selectedAttribute, value: props.replacementImage.url }
            ]
        };

        const res = await patchListingFieldsService(props.token, requestData);

        if (res) {
            setSaveModalData(res);
            setValidated(true);
        }
        setSaving(false);
    };

    // ---------- Update (mode: LIVE) — same call, reused ----------
    const handleUpdateReplacement = async () => {

        setSaving(true);

        const requestData = {
            asin: selectedAsin || asinOptions[0],
            sku: selectedSku,
            mode: "LIVE",
            integerId: props.integerId,
            updates: [
                { field: selectedAttribute, value: props.replacementImage.url }
            ],
            moveToAssetGallery: true,

            moveToAssetGalleryData: props.moveToAssetGalleryData
            // assetIdentifier: props.assetIdentifier
            
        };

        const res = await patchListingFieldsService(props.token, requestData);

        if (res) {
            setSaveModalData(res);

            if (!res.issues || res.issues.length === 0) {
                // reflect the new image immediately in this card
                setImageAttributes((prev) =>
                    prev.map((item) =>
                        item.attribute === selectedAttribute
                            ? { ...item, imageUrl: props.replacementImage.url }
                            : item
                    )
                );
            }
        }
        setSaving(false);
    };

    // Auto-scroll to the validation result, right under the Validate button
    useEffect(() => {
        if (saveModalData && validateSectionRef.current) {
            validateSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [saveModalData]);

    const isListingValid =
        saveModalData &&
        saveModalData.type !== "WARNING" &&
        saveModalData.mode === "VALIDATION_PREVIEW" &&
        (!saveModalData.issues || saveModalData.issues.length === 0);

    const canValidate =
        !!selectedAttribute &&
        !!(props.replacementImage && props.replacementImage.url) &&
        !saving;

    return (
        <>
            <Row>
                <Col md={12}>
                    <p className="mb-4">
                        Please choose the action you would like to perform.
                    </p>
                </Col>

                <Col md={12}>
                    <Row className="d-flex justify-content-between">

                        <Col md={6} className="mb-2 justify-content-center">
                            <Button
                                className="btn-background-color w-75"
                                onClick={() => {
                                    props.setIsMoveAssetsPopup(false);
                                    props.handleMoveToAssetgallery();
                                }}
                            >
                                Move to Asset Gallery
                            </Button>
                        </Col>

                        <Col md={6} className="mb-2 justify-content-center">
                            <Button
                                className="btn-background-color w-75"
                                onClick={handleUpdateAmazonListing}
                            >
                                Update Amazon Listing
                            </Button>
                        </Col>

                    </Row>
                </Col>
                <Col md={12} className='ml-1 mt-3'>
                    <p>⚠ Note: After the Amazon Listing is updated successfully, the asset will be automatically moved to the Asset Gallery.</p>
                </Col>

            </Row>
            <CustomModal
                isOpen={isUpdateListingPopupOpen}
                toggle={() => setIsUpdateListingPopupOpen(false)}
                size="xl"
                className="curve-popup"
                header="Update Amazon Listing"
            >
                <Card className="curve-popup p-3 border-0">
                    <Row>
                        {asinOptions.length > 1 && (
                            <Col md={4} className="mb-3">
                                <Label><b>ASIN</b></Label>
                                <Input
                                    type="select"
                                    value={selectedAsin}
                                    onChange={(e) => {
                                        const asin = e.target.value;
                                        setSelectedAsin(asin);
                                        setSelectedSku("");
                                        setSkuOptions(skuData[asin] || []);
                                        resetImageState();
                                    }}
                                >
                                    <option value="">Select ASIN</option>
                                    {asinOptions.map((asin) => (
                                        <option key={asin} value={asin}>{asin}</option>
                                    ))}
                                </Input>
                            </Col>
                        )}

                        <Col md={4} className="mb-3">
                            <Label><b>SKU</b></Label>
                            <Input
                                type="select"
                                value={selectedSku}
                                onChange={handleSkuChange}
                            >
                                <option value="">Select SKU</option>
                                {skuOptions.map((sku) => (
                                    <option key={sku} value={sku}>{sku}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Label>Replacement Image</Label>

                            <div
                                style={{
                                    height: "45px",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <img
                                    src={props.replacementImage.url}
                                    alt="Replacement"
                                    style={{
                                        width: "90px",
                                        height: "52px",
                                        objectFit: "contain",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        padding: "2px",
                                        background: "#fff"
                                    }}
                                />
                            </div>
                        </Col>

                                    {/* ---------- Validation result — reused optimization module UI, shown inline right below the button ---------- */}
                                    {saveModalData && (
                                        <Col md={12} className="mt-4" ref={validateSectionRef}>
                                            {saveModalData.type === "WARNING" ? (
                                                <div className="text-warning fw-bold fs-5 text-center mt-4">
                                                    {saveModalData.message}
                                                </div>
                                            ) : (
                                                <AmazonProductListingUpdatePopup
                                                    data={saveModalData}
                                                    // onUpdate={handleUpdateReplacement}
                                                    isListingValid={isListingValid}
                                                    saving={saving}
                                                    mode={saveModalData.mode}
                                                />
                                            )}
                                        </Col>
                                    )}
                        {isLoadingImages && (
                            <Col md={12} className="text-center my-4">
                                <Spinner color="primary" />
                                <p className="mt-2">Fetching listing images…</p>
                            </Col>
                        )}

                        {!isLoadingImages && fetchError && (
                            <Col md={12} className="my-3">
                                <p className="text-danger">{fetchError}</p>
                            </Col>
                        )}

                        {!isLoadingImages && imageAttributes.length > 0 && (
                            <Col md={12} className="mt-3">
                                <div className="d-flex justify-content-between mr-3"> <h5><b>Amazon Listing Images</b></h5>
                                
                                {/* Single Validate button below the entire image grid */}
                                {/* <Col md={12} className="mt-2 text-center"> */}
                                    <Button
                                        className="btn-background-color"
                                        disabled={!canValidate || validated}
                                        onClick={handleValidateReplacement}
                                    >
                                        {saving ? "Validating…" : "Validate"}
                                    </Button>
                                {/* </Col> */}
                                </div>
                               
                                <Col md={12} className="mt-4">
                                    <div className="mb-3">
                                        <strong>
                                            Click the Amazon listing image you want to replace with the replacement image.
                                        </strong>
                                    </div>
                                    <Row>
                                        {imageAttributes.map((item) => {

                                            const isSelected = selectedAttribute === item.attribute;

                                            return (
                                                <Col
                                                    md={3}
                                                    sm={4}
                                                    xs={6}
                                                    key={item.attribute}
                                                    className="mb-4 text-center"
                                                >
                                                    <Card
                                                        style={{
                                                            cursor: "pointer",
                                                            border: isSelected
                                                                ? "2px solid #007bff"
                                                                : "1px solid #ddd",
                                                            boxShadow: isSelected
                                                                ? "0 0 0 2px rgba(0,123,255,0.25)"
                                                                : "none"
                                                        }}
                                                        onClick={() => handleSelectAttribute(item.attribute)}
                                                    >
                                                        {item.imageUrl ? (
                                                            <img
                                                                src={item.imageUrl}
                                                                alt={item.attribute}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "170px",
                                                                    objectFit: "contain"
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    width: "100%",
                                                                    height: "170px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    background: "#f8f9fa",
                                                                    color: "#999",
                                                                    fontSize: "12px"
                                                                }}
                                                            >
                                                                No image set
                                                            </div>
                                                        )}

                                                        <div
                                                            className="mt-2 px-2"
                                                            style={{
                                                                fontSize: "12px",
                                                                wordBreak: "break-word"
                                                            }}
                                                        >
                                                            {item.attribute.replace(/_/g, " ")}
                                                            {isSelected && (
                                                                <div style={{ color: "#007bff" }}>Selected</div>
                                                            )}
                                                        </div>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>


                                </Col>
                            </Col>
                        )}
                    </Row>
                </Card>
            </CustomModal>
        </>
    );
};

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
    storeOverview: state.accountOverview.store.storeOverview,
    UserPermission: state.accountOverview.store.StoreUserPermission,
    accessLevel: state.loggedUser.accessLevel,
});

export default connect(mapStateToProps)(AmazonListingImageUpdatePopup);